import { useState, useRef, useEffect, useId } from 'react'
import { filterFormationsByQuery } from '../utils/formationsFilter'
import './FormationInstrumentPicker.css'

const MAX_SUGGESTIONS = 80

/**
 * Sélection multi-formations avec recherche et suggestions (liste maître + priorité corpus optionnelle).
 *
 * @param {{ id: string, label: string }[]} masterList
 * @param {string[]} value - ids sélectionnés
 * @param {(ids: string[]) => void} onChange
 * @param {string[]} [prioritizedIds] - dans le catalogue en premier
 * @param {'modal'|'freeMode'|'dashboard'} [theme]
 * @param {string} [hint]
 */
export function FormationInstrumentPicker({
  masterList,
  value,
  onChange,
  prioritizedIds = [],
  disabled = false,
  theme = 'modal',
  hint,
  showClearAll = true
}) {
  const uid = useId()
  const listboxId = `${uid}-listbox`
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)
  const wrapRef = useRef(null)

  const available = masterList.filter((f) => !value.includes(f.id))
  const suggestions = filterFormationsByQuery(available, query, prioritizedIds).slice(0, MAX_SUGGESTIONS)

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  function add(id) {
    if (value.includes(id)) return
    onChange([...value, id])
    setQuery('')
    setOpen(false)
    inputRef.current?.focus()
  }

  function remove(id) {
    onChange(value.filter((x) => x !== id))
  }

  const onKeyDown = (e) => {
    if (disabled) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        setHighlight(0)
      } else {
        setHighlight((h) => Math.min(h + 1, Math.max(0, suggestions.length - 1)))
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter' && open && suggestions.length > 0) {
      e.preventDefault()
      add(suggestions[highlight].id)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const selectedItems = value
    .map((id) => masterList.find((f) => f.id === id))
    .filter(Boolean)

  const rootClass = `formation-picker formation-picker--${theme}`

  return (
    <div className={rootClass} ref={wrapRef}>
      <div className="formation-picker-selected">
        {selectedItems.map((f) => (
          <button
            key={f.id}
            type="button"
            className="formation-picker-chip formation-picker-chip--selected"
            onClick={() => remove(f.id)}
            disabled={disabled}
            aria-label={`Retirer ${f.label}`}
          >
            <span>{f.label}</span>
            <span className="formation-picker-chip-remove" aria-hidden="true">
              ×
            </span>
          </button>
        ))}
        {showClearAll && value.length > 0 && (
          <button
            type="button"
            className="formation-picker-clear"
            onClick={() => onChange([])}
            disabled={disabled}
          >
            Tout retirer
          </button>
        )}
      </div>

      <div className="formation-picker-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="formation-picker-input"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setHighlight(0)
            setOpen(true)
          }}
          onFocus={() => {
            setHighlight(0)
            setOpen(true)
          }}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder="Rechercher un instrument ou un effectif…"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          role="combobox"
        />
        {open && suggestions.length > 0 && (
          <ul id={listboxId} className="formation-picker-suggestions" role="listbox">
            {suggestions.map((f, i) => (
              <li key={f.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  className={`formation-picker-suggestion ${i === highlight ? 'formation-picker-suggestion--active' : ''}`}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(f.id)}
                >
                  {f.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {hint && <small className="formation-picker-hint">{hint}</small>}
    </div>
  )
}
